import React, { useEffect, useRef } from "react";
import { Star } from "lucide-react";
import { motion, animate, Variants } from "framer-motion";
import { cn } from "@/lib/utils";

interface ReviewSummaryCardProps {
  rating: number;
  reviewCount: number;
  maxRating?: number;
  summaryText: string;
  className?: string;
}

export const ReviewSummaryCard: React.FC<ReviewSummaryCardProps> = ({
  rating,
  reviewCount,
  maxRating = 5,
  summaryText,
  className,
}) => {
  const ratingRef = useRef<HTMLSpanElement>(null);
  const reviewCountRef = useRef<HTMLSpanElement>(null);
  const yearsRef = useRef<HTMLSpanElement>(null);
  const maximaRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let isCycling = true;

    const ratingControl = animate(0, rating, {
      duration: 1.5,
      ease: "easeOut",
      onUpdate(value) {
        if (ratingRef.current) {
          ratingRef.current.textContent = value.toFixed(1);
        }
      },
    });

    const reviewCountControl = animate(0, reviewCount, {
      duration: 4.0,
      ease: "easeOut",
      onUpdate(value) {
        if (reviewCountRef.current) {
          reviewCountRef.current.textContent =
            "+" + new Intl.NumberFormat("pt-BR").format(Math.round(value));
        }
      },
    });

    const yearsControl = animate(0, 10, {
      duration: 4.0,
      ease: "easeOut",
      onUpdate(value) {
        if (yearsRef.current) {
          yearsRef.current.textContent = "+" + Math.round(value);
        }
      },
    });

    const cyclePulse = async () => {
      // Wait for counting to finish
      await new Promise((resolve) => setTimeout(resolve, 4000));

      while (isCycling) {
        const targets = [
          maximaRef.current,
          yearsRef.current,
          reviewCountRef.current,
        ];
        for (const target of targets) {
          if (!isCycling) break;
          if (target) {
            await animate(
              target,
              {
                scale: [1, 1.15, 1],
                color: ["#ffffff", "hsl(37, 44.5%, 48%)", "#ffffff"],
                textShadow: [
                  "0 0 0px rgba(255,255,255,0)",
                  "0 0 20px hsl(37, 44.5%, 48%)",
                  "0 0 0px rgba(255,255,255,0)",
                ],
              },
              {
                duration: 1.5,
                ease: "easeInOut",
              },
            );
            if (!isCycling) break;
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }
    };

    cyclePulse();

    return () => {
      ratingControl.stop();
      reviewCountControl.stop();
      yearsControl.stop();
      isCycling = false;
    };
  }, [rating, reviewCount]);

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const starVariants: Variants = {
    hidden: { opacity: 0, scale: 0.3, rotate: -45 },
    visible: (i: number) => ({
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200,
        delay: 0.8 + i * 0.15,
      },
    }),
  };

  return (
    <motion.div
      className={cn(
        "w-full max-w-[380px] rounded-[1.25rem] border border-white/20 bg-[#253046]/80 backdrop-blur-md py-6 px-4 text-center shadow-2xl",
        "flex flex-col items-center justify-center",
        className,
      )}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      aria-label={`Avaliação: ${rating} de ${maxRating} com base em ${reviewCount} avaliações.`}
    >
      <h2 className="text-[3.5rem] leading-none font-extrabold tracking-tight text-white mb-2">
        <span ref={ratingRef}>0.0</span>
      </h2>

      <div className="flex items-center gap-1">
        {Array.from({ length: maxRating }, (_, i) => (
          <motion.div
            key={i}
            custom={i}
            variants={starVariants}
            whileHover={{
              scale: 1.3,
              rotate: 15,
              transition: { type: "spring", stiffness: 300 },
            }}
          >
            <Star
              className={cn(
                "h-8 w-8",
                rating >= i + 1
                  ? "text-[#ffc107] fill-[#ffc107]"
                  : "text-muted-foreground/50",
              )}
            />
          </motion.div>
        ))}
      </div>

      <div className="mt-3 font-medium text-white/90 leading-snug text-base pl-[10px] pr-[10px] flex flex-col gap-1">
        <span>
          Nota{" "}
          <span
            ref={maximaRef}
            className="font-bold inline-block origin-center"
          >
            MÁXIMA
          </span>{" "}
          no Google
        </span>
        <span>
          <span ref={yearsRef} className="font-bold inline-block origin-center">
            +10
          </span>{" "}
          anos de experiência
        </span>
        <span>
          <span
            ref={reviewCountRef}
            className="font-bold inline-block origin-center"
          >
            +3.000
          </span>{" "}
          clientes
        </span>
      </div>
    </motion.div>
  );
};
